import { useState, useEffect, useMemo } from 'react';
import Weekly from './components/Weekly';
import Monthly from './components/Monthly';
import Prescription from './components/Prescription';
import OneTime from './components/OneTime';
import ViewTabs from './components/ViewTabs';
import EventModal from './components/EventModal';
import TimezoneModal from './components/TimezoneModal';
import { timezoneAliases } from './components/TimezoneModal';
import {
  getLocalStorageCalendar,
  saveLocalEvent,
  deleteLocalEvent,
} from './localStorage';

function App2({ trads, user, langUrl, activeEvents }) {
  const [calendar, setCalendar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showTimezoneModal, setShowTimezoneModal] = useState(false);
  const [timezoneUpdated, setTimezoneUpdated] = useState('');
  const viewKeys = ['oneTime', 'weekly', 'monthly', 'prescription'];
  const tabTestIds = {
    oneTime: 'one-time-tab',
    weekly: 'weekly-collapse-button',
    monthly: 'monthly-collapse-button',
    prescription: 'prescription-tab',
  };
  const defaultView = 'oneTime';
  const [selectedView, setSelectedView] = useState(() => {
    try {
      const stored = localStorage.getItem('smsscheduler_selected_view');
      if (stored && viewKeys.includes(stored)) return stored;
    } catch (e) {}
    return defaultView;
  });

  const lang = user?.favoriteLang || 'en';
  const t = trads?.index || {};

  useEffect(() => {
    try {
      localStorage.setItem('smsscheduler_selected_view', selectedView);
    } catch (e) {}
  }, [selectedView]);

  useEffect(() => {
    async function fetchCalendar() {
      try {
        if (!user) {
          const localCalendar = getLocalStorageCalendar();
          setCalendar(localCalendar);
        } else {
          const res = await fetch('/api/calendars/get-calendar');
          const data = await res.json();
          if (data.success && data.calendar) {
            setCalendar(data.calendar);
          }
        }
      } catch (err) {
        console.error('Failed to fetch calendar:', err);
      } finally {
        setLoading(false);
      }
    }
    fetchCalendar();
  }, [timezoneUpdated, user]);

  const getEventsForDay = (dayIndex) => {
    if (!calendar?.events) return [];
    return calendar.events.filter((e) => e.days?.includes(dayIndex));
  };

  const getDayName = (dayIndex) => {
    // Backend convention: 0=Monday, 1=Tuesday, ..., 6=Sunday
    return (
      t.days?.[dayIndex] ||
      [
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday',
        'Saturday',
        'Sunday',
      ][dayIndex]
    );
  };

  const inferEventType = (event) => {
    if (event?.type) {
      return event.type;
    }
    if (event?.everyDays != null) {
      return 'sequence';
    }
    if (Array.isArray(event?.daysInMonth) && event.daysInMonth.length > 0) {
      return 'monthly';
    }
    if (Array.isArray(event?.days) && event.days.length > 0) {
      return 'weekly';
    }
    return 'oneTime';
  };

  const handleEventClick = (event) => {
    setSelectedEvent(event);
  };

  const handleAddEvent = (dayIndex) => {
    // Create a new event template with default 9:00 AM (540 minutes) and the clicked day
    const newEvent = {
      id: null,
      type: 'weekly',
      name: '',
      content: '',
      days: [dayIndex],
      times: [540], // 9:00 AM = 9 * 60 = 540 minutes
      groupIds: [],
      eventContacts: [],
    };
    setSelectedEvent(newEvent);
  };

  const handleEventSave = async (updatedEvent) => {
    if (!calendar?.id && user) {
      console.error('Missing user or calendar');
      return;
    }

    try {
      if (!user) {
        // Local storage save
        const result = saveLocalEvent(updatedEvent);
        if (result.success && result.event) {
          setCalendar((prev) => {
            const exists = prev.events.some((e) => e.id === result.event.id);
            if (exists) {
              return {
                ...prev,
                events: prev.events.map((e) =>
                  e.id === result.event.id ? result.event : e
                ),
              };
            } else {
              return { ...prev, events: [...prev.events, result.event] };
            }
          });
        }
      } else {
        // API save
        const payload = {
          id: updatedEvent.id,
          calendarId: calendar.id,
          type: inferEventType(updatedEvent),
          name: updatedEvent.name,
          content: updatedEvent.content,
          days: updatedEvent.days,
          daysInMonth: updatedEvent.daysInMonth,
          everyDays: updatedEvent.everyDays || null,
          times: updatedEvent.times,
          startsAt: updatedEvent.startsAt,
          stopsAt: updatedEvent.stopsAt,
          timezone: calendar.timezone || 'Europe/Paris',
          contacts: updatedEvent.eventContacts?.map((ec) => ec.contactId) || [],
          groupIds: updatedEvent.groupIds || [],
          incomingOccurrencesInUtc: updatedEvent.incomingOccurrencesInUtc,
        };

        const res = await fetch(`/api/${user.id}/update-or-create-event`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });

        const data = await res.json();
        if (data.success && data.event) {
          // Update local calendar state with the updated/new event
          setCalendar((prev) => {
            const exists = prev.events.some((e) => e.id === data.event.id);
            if (exists) {
              return {
                ...prev,
                events: prev.events.map((e) =>
                  e.id === data.event.id ? data.event : e
                ),
              };
            } else {
              return { ...prev, events: [...prev.events, data.event] };
            }
          });
        } else {
          console.error('Failed to save event:', data.error);
        }
      }
    } catch (err) {
      console.error('Error saving event:', err);
    }

    setSelectedEvent(null);
  };

  const handleEventDelete = async (eventId) => {
    if (!user) {
      deleteLocalEvent(eventId);
      setCalendar((prev) => ({
        ...prev,
        events: prev.events.filter((e) => e.id !== eventId),
      }));
      setSelectedEvent(null);
      return;
    }

    try {
      const res = await fetch(`/api/${user.id}/delete-event`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ eventId }),
      });

      const data = await res.json();
      if (!data.success) {
        console.error('Failed to delete event:', data.error);
        return;
      }

      setCalendar((prev) => ({
        ...prev,
        events: prev.events.filter((e) => e.id !== eventId),
      }));
      setSelectedEvent(null);
    } catch (err) {
      console.error('Error deleting event:', err);
    }
  };

  const viewTabs = useMemo(
    () => [
      {
        key: 'oneTime',
        label: t.oneTimeMessageTitle || 'Send or plan one message',
        icon: (
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M13 2L4 14h6l-1 8 9-12h-6l1-8z" />
          </svg>
        ),
      },
      {
        key: 'weekly',
        label: t.weeklyRecurringEvents || 'Weekly',
        icon: (
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="9" />
            <path d="M12 7v5l3 2" />
          </svg>
        ),
      },
      {
        key: 'monthly',
        label: t.monthlyRecurringEvents || 'Monthly',
        icon: (
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="3" y="4" width="18" height="18" rx="2" />
            <path d="M16 2v4" />
            <path d="M8 2v4" />
            <path d="M3 10h18" />
          </svg>
        ),
      },
      {
        key: 'prescription',
        label: t.prescriptionTitle || 'Prescription',
        icon: (
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M14 9V5a4 4 0 0 0-8 0v4" />
            <rect x="6" y="9" width="12" height="11" rx="2" />
            <path d="M10 13h4" />
            <path d="M12 11v4" />
          </svg>
        ),
      },
    ],
    [t]
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500">
        {t.loading || 'Loading...'}
      </div>
    );
  }

  // Removed the !user check block

  let displayedTimezone = calendar?.timezone;
  for (const alias of Object.keys(timezoneAliases)) {
    if (timezoneAliases[alias] === displayedTimezone) {
      displayedTimezone = alias;
      break;
    }
  }
  const viewComponents = {
    oneTime: (
      <OneTime
        calendar={calendar}
        trads={trads}
        langUrl={langUrl}
        demoMode={!user}
        onEventSave={handleEventSave}
        onEventDelete={handleEventDelete}
      />
    ),
    weekly: (
      <Weekly
        getDayName={getDayName}
        getEventsForDay={getEventsForDay}
        trads={trads}
        lang={lang}
        demoMode={!user}
        onEventClick={handleEventClick}
        onAddEvent={handleAddEvent}
      />
    ),
    monthly: (
      <Monthly
        recurring={true}
        calendar={calendar}
        user={user}
        demoMode={!user}
        trads={trads}
        lang={lang}
        langUrl={langUrl}
        getDayName={getDayName}
        onEventSave={handleEventSave}
        onEventDelete={handleEventDelete}
      />
    ),
    prescription: (
      <Prescription
        calendar={calendar}
        user={user}
        demoMode={!user}
        trads={trads}
        lang={lang}
        langUrl={langUrl}
        onEventSave={handleEventSave}
        onEventDelete={handleEventDelete}
      />
    ),
  };

  const currentView = viewComponents[selectedView] || viewComponents.oneTime;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="flex items-center justify-between mb-6 mt-2">
        <div>
          <span className="text-lg font-normal text-gray-900">
            {trads.index.timezone} :{' '}
          </span>
          <a
            className="text-lg font-medium text-gray-900 cursor-pointer no-underline hover:underline hover:font-bold"
            type="button"
            onClick={() => setShowTimezoneModal(true)}
          >
            {displayedTimezone || 'Europe/Paris'}
          </a>
        </div>
      </div>

      <div className="mb-6 -mx-2 sm:mx-0">
        <ViewTabs
          viewTabs={viewTabs}
          selectedView={selectedView}
          activeEvents={activeEvents}
          onSelect={setSelectedView}
          viewKeys={viewKeys}
          tabTestIds={tabTestIds}
        />
      </div>

      <div className="flex flex-col gap-6">{currentView}</div>

      <EventModal
        event={selectedEvent}
        isOpen={!!selectedEvent}
        onClose={() => setSelectedEvent(null)}
        onSave={handleEventSave}
        onDelete={handleEventDelete}
        langUrl={langUrl}
        demoMode={!user}
        trads={trads}
        getDayName={getDayName}
      />

      <TimezoneModal
        isOpen={showTimezoneModal}
        calendarId={calendar.id}
        onClose={() => setShowTimezoneModal(false)}
        onSave={(timezone) => {
          setTimezoneUpdated(timezone);
        }}
        trads={trads}
      />
    </div>
  );
}

export default App2;
